import React, { useContext, useEffect } from 'react';
import appContext from '../../../../context/app/appContext';
import CreateASpoolContext from '../../../../context/createASpool/createASpoolContext';
import themeContext from '../../../../context/theme/themeContext';
import InfoPopUp from '../../../../components/InfoPopUp';
import Loading from '../../../../components/Loading';
import { StrategyStrength, SupportedChains } from '../../../../config/constants';
import { CheckboxTickIcon, ErrorIcon } from '../../../../assets/icons';
import { TabsContext } from '../../../../components/Tabs';
import { useWeb3React } from '@web3-react/core';

const HIDDEN_STRATEGIES = [
    '0x9e9d4d747a3dfe3d135077cfa1baf98599cf5b80',
    '0xe12286c9b881ea429cfd9e6ca0fb666c819981b0',
    '0x335bd363fe258f0af80a2b664c1c9b45ab6fce9d',
    '0x94df827cadc7101452a24161d6bb2f6da2c6f698',
    '0x5713298ea1c643ad4b6e4376ea163505bda41381',
    '0xbfa7646f4a20cc303501ab7c9ee4416401ccd532',
    '0x413e7caaf85136ede266025bc24bc60f089c0687',
    '0x9e177bb9f107bffdc0f9b512f969f6c071d57d76',
    '0x47b2fa8b3a5be5644a10c8acb9f0e0924b2eefb3',
    '0x31c2f932052f7861ac3f301c584d6888b6e6896d',
];

const Strategies: React.FC = () => {
    const {
        selectedAsset,
        selectedRiskModel,
        selectedStrategies,
        setStepCompletedStatus,
        addStrategy,
        removeStrategy,
    } = useContext(CreateASpoolContext);

    const { isLoading, strategies, getStrategies } = useContext(appContext);

    const { chainId } = useWeb3React();

    const { theme } = useContext(themeContext);

    const { updateHeight } = useContext(TabsContext);

    const { chainStrategies } = useContext(appContext);

    const handleClick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        ~selectedStrategies.indexOf(value) ? removeStrategy(value) : addStrategy(value);
    };

    useEffect(() => {
        updateHeight();
    }, [isLoading, updateHeight]);

    useEffect(() => {
        if (!selectedAsset || !selectedRiskModel) return;

        getStrategies(selectedAsset, selectedRiskModel);
    }, [selectedAsset, selectedRiskModel, getStrategies]);

    useEffect(() => {
        if (selectedStrategies.length) {
            setStepCompletedStatus(3, true);
            return;
        }
        setStepCompletedStatus(3, false);
    }, [selectedStrategies, setStepCompletedStatus]);

    const getStrategyStrength = (strength: string) => {
        const strategiesLength = selectedStrategies.length;

        switch (true) {
            case strategiesLength >= 1 &&
                strategiesLength <= 3 &&
                strength === StrategyStrength.WEAK:
                return StrategyStrength.WEAK;
            case strategiesLength > 3 &&
                strategiesLength <= 6 &&
                strength === StrategyStrength.MEDIUM:
                return StrategyStrength.MEDIUM;
            case strategiesLength > 6 && strength === StrategyStrength.STRONG:
                return StrategyStrength.STRONG;
            default:
                return '';
        }
    };

    return (
        <div className={`strategies ${theme}`}>
            <span>
                Select one or more strategies from the list below. The list of available
                strategies is based on your answers from step 1 and 2
            </span>

            <Loading isLoading={isLoading.strategies} text='Loading Strategies...'>
                {strategies ? (
                    strategies.length ? (
                        <div
                            className={`strategies-list  ${
                                chainId === SupportedChains.ARBITRUM_ONE ? 'arbitrum' : ''
                            }`}
                        >
                            {strategies.map((strategyAddress) => {
                                if (
                                    HIDDEN_STRATEGIES.includes(
                                        strategyAddress.toLowerCase()
                                    )
                                )
                                    return null;
                                const strategy =
                                    chainStrategies[strategyAddress.toLowerCase()];
                                return (
                                    <label
                                        key={strategyAddress}
                                        className={
                                            ~selectedStrategies.indexOf(strategyAddress)
                                                ? 'selected'
                                                : ''
                                        }
                                    >
                                        <CheckboxTickIcon />
                                        <span>{strategy?.name ?? 'Strategy'}</span>
                                        <input
                                            type='checkbox'
                                            name='address'
                                            value={strategyAddress}
                                            onChange={handleClick}
                                        />
                                        {chainStrategies[strategyAddress.toLowerCase()]
                                            ?.negativeApyText && (
                                            <InfoPopUp
                                                trigger={<ErrorIcon />}
                                                className='warning-pop-up'
                                            >
                                                {
                                                    chainStrategies[
                                                        strategyAddress.toLowerCase()
                                                    ]?.negativeApyText
                                                }
                                            </InfoPopUp>
                                        )}
                                        <InfoPopUp>
                                            {strategy?.detail ?? 'Strategy Details'}
                                        </InfoPopUp>
                                    </label>
                                );
                            })}
                        </div>
                    ) : (
                        <div className='fallback'>
                            <ErrorIcon />
                            <span>No strategies found...</span>
                        </div>
                    )
                ) : (
                    <div className='fallback'>
                        <ErrorIcon />
                        <span>Unexpected error occurred while loading strategies...</span>
                    </div>
                )}
            </Loading>

            {chainId === SupportedChains.MAINNET && (
                <div className='diversification-strategy'>
                    <span>Your Diversification Strategy is:</span>
                    <div className='diversification-strength'>
                        <div
                            className={`strength ${getStrategyStrength(
                                StrategyStrength.WEAK
                            )}`}
                        >
                            <span>{StrategyStrength.WEAK}</span>
                            <div />
                        </div>
                        <div
                            className={`strength ${getStrategyStrength(
                                StrategyStrength.MEDIUM
                            )}`}
                        >
                            <span>{StrategyStrength.MEDIUM}</span>
                            <div />
                        </div>
                        <div
                            className={`strength ${getStrategyStrength(
                                StrategyStrength.STRONG
                            )}`}
                        >
                            <span>{StrategyStrength.STRONG}</span>
                            <div />
                        </div>
                        <div className='suggestion'>
                            <span>
                                It is suggested to decrease the risk of your Spool by
                                selecting multiple strategies to create a stronger
                                diversification strategy.
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Strategies;
